import { Icon } from "@iconify/react";
import {
  useQuery,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Button,
  Card,
  Drawer,
  Flex,
  Form,
  Grid,
  Input,
  Pagination,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { rewardService } from "../../../services/reward.service";
import TableAction from "../../../components/TableAction";
import useTitleStore from "../../../store/titleStore";
import useDebounce from "../../../hooks/useDebounce";
import { useTableHeight } from "../../../hooks/useTableHeight";

const { Text, Paragraph } = Typography;

export default function RewardIndex() {
  const breakpoint = Grid.useBreakpoint();

  const [form] = Form.useForm();

  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();

  const tableHeight = useTableHeight();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null,
  });
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: [
      {
        operator: "orWhere",
        value: [
          {
            field: "name",
            operator: "like",
            value: debouncedKeyword,
          },
          {
            field: "description",
            operator: "like",
            value: debouncedKeyword,
          },
        ],
      },
    ],
  });

  useEffect(() => {
    setTitle("Reward");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          operator: "orWhere",
          value: [
            {
              field: "name",
              operator: "like",
              value: debouncedKeyword,
            },
            {
              field: "description",
              operator: "like",
              value: debouncedKeyword,
            },
          ],
        },
      ],
    }));
  }, [debouncedKeyword]);

  const { data: rewardData, isLoading, isRefetching } = useQuery({
    queryKey: ["rewards", dataParams],
    queryFn: () => rewardService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { data: rewardDetail } = useQuery({
    queryKey: ["reward", drawerOpt.id],
    queryFn: () => rewardService.getById(drawerOpt.id),
    enabled: !!drawerOpt.id,
  });

  useEffect(() => {
    if (drawerOpt.id && rewardDetail?.data) {
      form.setFieldsValue({
        ...rewardDetail.data,
        available: rewardDetail.data.available ?? true,
      });
    }
  }, [drawerOpt.id, rewardDetail, form]);

  const storeMutation = useMutation({
    mutationFn: (val) => rewardService.store(val),
    onSuccess: () => {
      queryClient.invalidateQueries("rewards");
      messageApi.success("Data berhasil ditambah");
      handleCloseDrawer();
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (val) => rewardService.update(val),
    onSuccess: () => {
      queryClient.invalidateQueries("rewards");
      messageApi.success("Data berhasil diubah");
      handleCloseDrawer();
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan");
    },
  });

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => rewardService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries("rewards");
      messageApi.success("Data berhasil dihapus");
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan");
    },
  });

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const handleEdit = (record) => {
    setDrawerOpt({ open: true, id: record.id });
  };

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({ available: true });
    setDrawerOpt({ open: true, id: null });
  };

  const handleCloseDrawer = () => {
    form.resetFields();
    setDrawerOpt({ open: false, id: null });
  };

  const handleSubmit = (val) => {
    if (drawerOpt.id) updateMutation.mutate({ id: drawerOpt.id, ...val });
    else storeMutation.mutate(val);
  };

  const rewards = rewardData?.data;
  const pagination = rewardData?.pagination || { total: 0, page: 1 };

  const columns = [
    {
      title: "No",
      align: "center",
      width: 60,
      fixed: breakpoint.md && "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Nama Reward",
      dataIndex: "name",
      width: 260,
      fixed: breakpoint.md && "left",
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "Deskripsi",
      dataIndex: "description",
      ellipsis: true,
      render: (val) =>
        val ? (
          <Paragraph
            type="secondary"
            style={{ marginBottom: 0 }}
            ellipsis={{ rows: 2 }}
          >
            {val}
          </Paragraph>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Status Ketersediaan",
      dataIndex: "available",
      width: 200,
      render: (val) => (
        <Tag color={val ? "green" : "red"}>
          {val ? "Tersedia" : "Tidak Tersedia"}
        </Tag>
      ),
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 120,
      fixed: "right",
      render: (record) => (
        <TableAction
          onClickEdit={() => handleEdit(record)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Flex
          gap={20}
          justify="space-between"
          style={{ paddingInline: 24, marginBottom: 24 }}
          wrap="wrap"
        >
          <Input
            style={{ width: 360, maxWidth: "100%" }}
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari berdasarkan nama atau deskripsi"
            onChange={(e) => setKeyword(e.target.value)}
            value={keyword}
            allowClear
          />
          <Button
            variant="solid"
            color="primary"
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={handleAdd}
            style={{ minWidth: 38 }}
          >
            {breakpoint.md && "Tambah Reward"}
          </Button>
        </Flex>
        <Table
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={"id"}
          scroll={{ y: tableHeight }}
          dataSource={rewards}
          pagination={false}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={pagination.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={["10", "20", "50"]}
          current={pagination.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>

      <Drawer
        open={drawerOpt.open}
        title={drawerOpt.id ? "Ubah Data Reward" : "Tambah Data Reward"}
        onClose={handleCloseDrawer}
        width={breakpoint.md ? 560 : "100%"}
        footer={
          <Flex gap={16}>
            <Button
              block
              disabled={storeMutation.isPending || updateMutation.isPending}
              onClick={handleCloseDrawer}
              variant="outlined"
              color="primary"
            >
              Batal
            </Button>
            <Button
              block
              variant="solid"
              color="primary"
              onClick={() => form.submit()}
              disabled={storeMutation.isPending || updateMutation.isPending}
              loading={storeMutation.isPending || updateMutation.isPending}
            >
              Simpan
            </Button>
          </Flex>
        }
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name={"id"} hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name={"name"}
            label="Nama Reward"
            rules={[{ required: true, message: "Nama wajib diisi" }]}
          >
            <Input placeholder="Contoh: Potongan Tagihan Air Rp 50.000" />
          </Form.Item>
          <Form.Item
            name={"description"}
            label="Deskripsi"
            rules={[{ required: true, message: "Deskripsi wajib diisi" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Tambahkan deskripsi singkat untuk reward ini..."
            />
          </Form.Item>
          <Form.Item
            name={"available"}
            label="Status Ketersediaan"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value === true || value === false
                    ? Promise.resolve()
                    : Promise.reject(new Error("Status wajib diisi")),
              },
            ]}
            initialValue={true}
          >
            <Switch checkedChildren="Tersedia" unCheckedChildren="Tidak Tersedia" />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}